import { useRecoilValue } from 'recoil';
import { useGetAPI, usePostAPI, usePutAPI } from '@api/useAPI';
import { XCustomerID } from '@state';
import {
	AJAXError,
	APICallProps,
	TItemType,
} from '@types';

/**
 * The Customer's live Item types, sorted by name, in one unpaginated body. Skips the client's
 * duplicate-request check, which goes by URL alone: it would cancel one Customer's request as a
 * repeat of another's, since only the header tells them apart.
 */
export const useGetItemTypes = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		callAPI
	} = useGetAPI({
		endpoint: '/api/item-type',
		domain: 'ShipmentAPI',
		headers,
		avoidCache: true
	});

	return {
		loadingItemTypes: isLoading,
		callGetItemTypes: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TItemType[] } | null;
			error: AJAXError | null;
		} | undefined>,
	}
}

export const useCreateItemType = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		callAPI
	} = usePostAPI({
		endpoint: '/api/item-type',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		creatingItemType: isLoading,
		callCreateItemType: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TItemType } | null;
			error: AJAXError | null;
		} | undefined>,
	}
}

/** Edits the Item type in place, so the change reaches every Rate card priced on it. */
export const useUpdateItemType = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		callAPI
	} = usePutAPI({
		endpoint: '/api/item-type/{1}',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		updatingItemType: isLoading,
		callUpdateItemType: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TItemType } | null;
			error: AJAXError | null;
		} | undefined>,
	}
}
